<template>
  <div class="billingAddressForm flex flex-col gap-2">
    <span class="block text-sm font-medium text-gray-900">
      Credit card billing address
    </span>
    <div
      role="radiogroup"
      aria-label="Billing address"
      class="grid grid-cols-2 gap-2"
    >
      <button
        type="button"
        role="radio"
        :aria-checked="addressType === 'current'"
        :class="tileClasses('current')"
        data-cy="billing-address-current"
        @click="addressType = 'current'"
      >
        <span class="font-medium">On file</span>
        <span class="text-xs text-gray-600 mt-0.5">
          {{ address.street || "—" }}<br />
          {{ address.city }}<span v-if="address.city">, </span
          >{{ address.state }} {{ address.zip_code }}
        </span>
      </button>
      <button
        type="button"
        role="radio"
        :aria-checked="addressType === 'new'"
        :class="tileClasses('new')"
        data-cy="billing-address-new"
        @click="addressType = 'new'"
      >
        <span class="font-medium">Different address</span>
        <span class="text-xs text-gray-600 mt-0.5"
          >Enter the card's billing address.</span
        >
      </button>
    </div>

    <div v-if="addressType === 'new'" class="flex flex-col gap-3 mt-2">
      <PaymentFormInput
        v-model="formValues.street"
        label="Address"
        :error-message="errorMessages.street"
        data-cy="street"
      />
      <div class="grid grid-cols-1 md:grid-cols-[1fr_140px_140px] gap-3">
        <PaymentFormInput
          v-model="formValues.city"
          label="City"
          :error-message="errorMessages.city"
          data-cy="city"
        />
        <PaymentFormSelect
          v-model="formValues.state"
          label="State"
          :options="STATE_OPTIONS"
          :error-message="errorMessages.state"
          data-cy="state"
        />
        <PaymentFormInput
          v-model="formValues.zip_code"
          label="Zip code"
          type="tel"
          placeholder="Zip"
          :error-message="errorMessages.zip_code"
          data-cy="zip-code"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { STATE_OPTIONS } from "serviceshift-ui/shared/src/lib/stateOptions";
import { ref } from "vue";

import PaymentFormInput from "@/components/PaymentModals/PaymentFormInput.vue";
import PaymentFormSelect from "@/components/PaymentModals/PaymentFormSelect.vue";

const props = defineProps({
  address: {
    type: Object as () => {
      city: string;
      state: string;
      street: string;
      zip_code: string;
    },
    default: null
  }
});
const addressType = ref<"current" | "new">("current");
const errorMessages = ref({
  street: "",
  zip_code: "",
  city: "",
  state: ""
});
const formValues = ref({
  street: "",
  zip_code: "",
  city: "",
  state: ""
});

function tileClasses(value: "current" | "new") {
  const base =
    "flex flex-col items-start text-left rounded-md border px-3 py-2 text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 cursor-pointer";
  if (addressType.value === value) {
    return `${base} border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-600`;
  }
  return `${base} border-gray-200 bg-white text-gray-700 hover:border-gray-400`;
}

function clearErrors() {
  Object.keys(errorMessages.value).forEach((key) => {
    errorMessages.value[key] = "";
  });
}

async function getData() {
  clearErrors();
  const data = formValues.value;
  if (addressType.value === "current") return props.address;
  if (!data.street) {
    const error = "Address is required";
    errorMessages.value.street = error;
    throw error;
  }
  if (!data.city) {
    const error = "City is required";
    errorMessages.value.city = error;
    throw error;
  }
  if (!data.state) {
    const error = "State is required";
    errorMessages.value.state = error;
    throw error;
  }
  if (!data.zip_code) {
    const error = "Zip code is required";
    errorMessages.value.zip_code = error;
    throw error;
  }
  return data;
}

defineExpose({
  getData
});
</script>
